import { IRootState, TWAction } from '@libs/types';
import { isAnyOf, Middleware, StoreEnhancer } from '@reduxjs/toolkit';
import { addTransitionType, startTransition, useLayoutEffect, useState } from 'react';
import { fetchScenario, TPageFields, updateScenario } from './features/utils/sharedActions';

export type TPageChange = {
    /** The action that requested the new page, when known. */
    action?: TWAction;
    /** Page-level fields (everything but `widgets`) of the page being left, as the backend sent them. */
    leftPage?: TPageFields;
};

/**
 * Decides how a page change animates: the transition types to tag it with (`addTransitionType`, matched by the
 * page `<ViewTransition>`), or nothing to apply the new page without a transition. Supplied by the app.
 */
export type TPageTransitionResolver = (change: TPageChange) => string[] | undefined;

/** Safety net: subscribers are never held longer than this, even if the page transition never commits. */
const HOLD_TIMEOUT_MS = 1000;

const pageCommitListeners = new Set<() => void>();
const heldNotificationFlushers = new Set<() => void>();

let resolver: TPageTransitionResolver | undefined;
let isHeld = false;
let hasHeldNotification = false;
let holdTimer: ReturnType<typeof setTimeout> | undefined;

const releaseNotifications = (): void => {
    clearTimeout(holdTimer);
    if (!isHeld) return;
    isHeld = false;
    if (hasHeldNotification) {
        hasHeldNotification = false;
        heldNotificationFlushers.forEach((flush) => flush());
    }
};

const holdNotifications = (): void => {
    isHeld = true;
    clearTimeout(holdTimer);
    holdTimer = setTimeout(releaseNotifications, HOLD_TIMEOUT_MS);
};

const isPageAction = isAnyOf(fetchScenario.fulfilled, updateScenario.fulfilled);
const isPageRequestAction = isAnyOf(fetchScenario.pending, updateScenario.pending);

/**
 * Only while an app has mounted `usePageTransition` with a resolver (consumer on phones). Everywhere else the store
 * behaves exactly as before. Reduced motion is honoured here: React doesn't do it for `<ViewTransition>`.
 */
const canAnimate = (): boolean =>
    !!resolver &&
    pageCommitListeners.size > 0 &&
    typeof document !== 'undefined' &&
    typeof document.startViewTransition === 'function' &&
    !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

const resolveTypes = (change: TPageChange): string[] | undefined => {
    try {
        return resolver?.(change);
    } catch (error) {
        console.error('pageTransitionMiddleware: the transition resolver failed, applying the page without a transition', error);
        return undefined;
    }
};

/**
 * Lets the store hold back subscriber notifications while a page change renders as a React Transition.
 *
 * react-redux reads the store through `useSyncExternalStore`, and React always re-renders a store notification
 * synchronously (SyncLane) — which never activates `<ViewTransition>`. Holding the notification leaves the page switch
 * to the transition-lane render started by the middleware: it reads the already-updated state through `getState`,
 * React snapshots the old page, commits the new one and animates. Held notifications are flushed right after that
 * commit (`usePageTransition`), or after `HOLD_TIMEOUT_MS` at the latest.
 */
export const pageTransitionEnhancer: StoreEnhancer = (createStore) => (reducer, preloadedState) => {
    const store = createStore(reducer, preloadedState);
    const listeners = new Set<() => void>();
    const notify = (): void => Array.from(listeners).forEach((listener) => listener());

    heldNotificationFlushers.add(notify);
    store.subscribe(() => {
        if (isHeld) {
            hasHeldNotification = true;
            return;
        }
        notify();
    });

    return {
        ...store,
        subscribe: (listener: () => void) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
};

/**
 * Applies a new scenario page inside `startTransition`, tagged with the types the app's resolver picked (see
 * `pageTransitionEnhancer`). Keeps the current page's fields in its closure, not in the state.
 */
export const pageTransitionMiddleware: Middleware<object, IRootState> = (api) => {
    let pageFields: TPageFields | undefined;
    // The latest page request. Tracked here rather than read from the slice's `pendingRequestId`, which widget-level
    // requests (`fetchJsonAsync`) overwrite too and which would make unrelated widget traffic skip the animation.
    let latestPageRequestId: string | undefined;

    return (next) => (action) => {
        if (isPageRequestAction(action)) {
            latestPageRequestId = action.meta.requestId;
            return next(action);
        }
        if (!isPageAction(action) || !action.payload) return next(action);

        const leftPage = pageFields;
        pageFields = action.payload.pageFields;
        if (!canAnimate()) return next(action);

        const prev = api.getState().scenario;
        const isNewPage = action.payload.scenarioId !== prev?.scenarioId || action.payload.currentPageId !== prev?.currentPageId;
        // The very first page has nothing to transition from.
        const isFirstPage = !prev?.rootWidgets?.length;
        // A newer page request already started: apply this page at once instead of racing it.
        const isSuperseded = !!latestPageRequestId && latestPageRequestId !== action.meta.requestId;
        if (!isNewPage || isFirstPage || isSuperseded) return next(action);

        const types = resolveTypes({ action: action.meta.arg.action, leftPage });
        if (!types?.length) return next(action);

        let result: unknown;
        holdNotifications();
        startTransition(() => {
            types.forEach((type) => addTransitionType(type));
            result = next(action);
            pageCommitListeners.forEach((listener) => listener());
        });
        return result;
    };
};

/**
 * Turns scenario page transitions on for the component that renders the page: re-renders it in the transition lane
 * on a page change, so a `<ViewTransition update={...}>` around the page animates it. The `<ViewTransition>` keeps
 * its identity (no `key`), so widgets are reconciled by `uid` as usual. Without a resolver nothing animates.
 */
export const usePageTransition = (resolve: TPageTransitionResolver | undefined): void => {
    const [commit, setCommit] = useState(0);

    useLayoutEffect(() => {
        if (!resolve) return;
        const listener = (): void => setCommit((value) => value + 1);
        resolver = resolve;
        pageCommitListeners.add(listener);
        return () => {
            pageCommitListeners.delete(listener);
            if (resolver === resolve) resolver = undefined;
            releaseNotifications();
        };
    }, [resolve]);

    // The transition with the new page has committed: let the rest of the app see the store update.
    useLayoutEffect(releaseNotifications, [commit]);
};
